import ClassNames from "classnames";
import { FunctionComponent } from "react";
import {
  checkIsPlaceholder,
  getPictureById,
} from "../../selectors/pictures.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/images-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  ImageSliderControlsPosition,
  ImagesModuleSettings,
  Modules,
  OverlayModuleSettings,
  TranslatedModule,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getModulesByParentId,
  getPlaceholderImage,
  getRecursiveOverlayModule,
  getTranslatedModule,
  keys,
} from "../../utils/utils.js";
import { minWidthBreakpoints } from "../ContainerQueries.js";
import ImageSlider from "../ImageSlider.js";
import ImagesSeparator from "../ImagesSeparator.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

interface Props extends BaseModuleProps<ImagesModuleSettings> {
  isInsideHeader?: boolean;
  parentScheme?: ColorScheme;
  fullHeight?: boolean;
  sliderControlsPosition?: ImageSliderControlsPosition;
  className?: string;
}

const ImagesModule: FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    pageId: translatedModulePageId,
    id: translatedModuleId,
    settings: { imagesType, columnsCount, textAlign, width },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  isPreview,
  isInsideHeader = false,
  parentScheme,
  pageId,
  activeModuleId,
  fullHeight = false,
  isFirstOnPage,
  queries,
  sliderControlsPosition = "middle",
  className,
}) => {
  const mediaLibraryLoaded = useAppSelector(
    (state) => state.loadStates.mediaLibrary.pictures === "loaded",
  );
  const site = useAppSelector((state) => getActiveSite(state.sites));
  const modules = useAppSelector((state) => state.modules);
  const pictures = useAppSelector((state) => state.mediaLibrary.pictures);
  const scheme = useAppSelector(
    (state) =>
      parentScheme ??
      getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );

  const imagesByParentId = !translatedModulePageId
    ? []
    : getModulesByParentId<ImageModuleSettings>(
        modules,
        translatedModuleId,
        translatedModulePageId,
        "ImageModule",
      ).reduce<TranslatedModule<ImageModuleSettings>[]>((images, module) => {
        const image = getTranslatedModule(module, languageId);
        const picture = getPictureById(pictures, image.settings.pictureId);
        // // If the are multiple placeholder images (e.g. due to deleted EasyChannel images)
        // // filter them out
        return checkIsPlaceholder(picture) ? images : [...images, image];
      }, []);

  const hasOverlaySubmodules = getHasOverlaySubmodules(
    modules,
    imagesByParentId,
    pageId,
  );

  const overlayModule = hasOverlaySubmodules
    ? undefined
    : getRecursiveOverlayModule({
        languageId,
        modules,
        pageId,
        parentId: translatedModuleId,
      });

  const images =
    imagesByParentId.length || !pageId
      ? imagesByParentId
      : [
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: translatedModuleId,
            index: 0,
          }),
        ];

  if (!mediaLibraryLoaded) return null;

  const currentWidthColumnsCount = getBreakpointImageColumnsCount(
    queries,
    columnsCount,
  );

  // Check if images fill all columns using one or more rows and show a separator
  // in that case, otherwise show a slider to avoid empty spaces among rows.
  // If the images fit in one row or don’t fill all columns in a row, always show
  // the a separator, not a slider.
  // On very narrow viewport widths (with max 1 column), always show a slider
  // if there are multiple images.
  const imagesFillColumns = !(images.length % currentWidthColumnsCount);
  const imagesFitInOneRow = images.length <= currentWidthColumnsCount;
  const isOneColumnAndMultipleImages =
    !queries["Query--small"] && images.length > 1;

  const isSeparator =
    ((imagesFillColumns && imagesType === "separator") || imagesFitInOneRow) &&
    !isOneColumnAndMultipleImages;

  return (
    <ModuleWithHeadings
      className={ClassNames(
        styles.ImagesModule,
        { [styles["ImagesModule--full-height"]]: fullHeight },
        className,
      )}
      id={translatedModule.id}
      colors={{ background: scheme.main.background }}
      title={!isInsideHeader ? title : undefined}
      subtitle={!isInsideHeader ? subtitle : undefined}
    >
      {!isInsideHeader && (title || subtitle) && (
        <div
          className={ClassNames(
            styles.ImagesModule__HeadingsWrapper,
            moduleBaseStyles.Module__Wrapper,
            moduleBaseStyles["Module__Wrapper--small-padding"],
          )}
        >
          <ModuleHeadings
            textAlign={textAlign}
            scheme={scheme}
            isFirstOnPage={isFirstOnPage}
            title={title}
            subtitle={subtitle}
          />
        </div>
      )}

      <div
        className={ClassNames(styles.ImagesModule__Inner, {
          [styles["ImagesModule__Inner--width-1"]]: width === 1,
          [styles["ImagesModule__Inner--width-2"]]: width === 2,
          [styles["ImagesModule__Inner--width-3"]]: width === 3,
          [styles["ImagesModule__Inner--width-4"]]: width === 4,
          [styles["ImagesModule__Inner--width-5"]]: width === 5,
        })}
      >
        {isSeparator ? (
          <ImagesSeparator
            pageId={pageId}
            images={images}
            settings={translatedModule.settings}
            isInsideHeader={isInsideHeader}
            scheme={scheme}
            isPreview={isPreview}
          />
        ) : (
          <ImageSlider
            pageId={pageId}
            isPreview={isPreview}
            images={images}
            translatedModule={translatedModule}
            isInsideHeader={isInsideHeader}
            scheme={scheme}
            overlayModule={overlayModule}
            activeModuleId={activeModuleId}
            slidesToShow={columnsCount}
            queries={queries}
            fullHeight={fullHeight}
            controlsPosition={sliderControlsPosition}
          />
        )}
      </div>
    </ModuleWithHeadings>
  );
};

const getHasOverlaySubmodules = (
  modules: Modules,
  images: TranslatedModule<ImageModuleSettings>[],
  pageId: string,
): boolean => {
  const hasOverlaySubmodules: boolean = !!images.reduce<number>(
    (count, { id }) => {
      const currentCount = getModulesByParentId<OverlayModuleSettings>(
        modules,
        id,
        pageId,
        "OverlayModule",
      );

      return count + currentCount.length;
    },
    0,
  );

  return hasOverlaySubmodules;
};

export const getBreakpointImageColumnsCount = (
  queries: ContainerQueries,
  columnsCountSetting: number,
): number => {
  const maxSlidesPerBreakpoint: { [key in keyof ContainerQueries]: number } = {
    "Query--small": 2,
    "Query--medium": 3,
    "Query--large": 4,
    "Query--xlarge": 5,
    "Query--xxlarge": 6,
  };

  const currentBreakpoint = keys(minWidthBreakpoints)
    .reverse()
    .find((key) => queries[key]);

  return currentBreakpoint
    ? Math.min(columnsCountSetting, maxSlidesPerBreakpoint[currentBreakpoint])
    : 1;
};

export default ImagesModule;
